/**
 * Block deprecation
 *
 * @see example block
 * @see https://developer.wordpress.org/block-editor/reference-guides/block-api/block-deprecation/
 * @see https://developer.wordpress.org/news/2023/03/block-deprecation-a-tutorial/
 */

import { useBlockProps, useInnerBlocksProps } from '@wordpress/block-editor';
import isEmpty from 'lodash/isEmpty';
import ConditionalWrapper from '../../components/ConditionalWrapper';
import { filterOutWhiteSpace } from '../utils/general-utils';

const v3 = {
  supports: {
    anchor: true,
    html: false
  },
  attributes: {
    image: {
      default: {},
      type: 'object'
    },
    mobileImage: {
      default: {},
      type: 'object'
    },
    imageSize: {
      default: 'large',
      type: 'string'
    },
    blurEffect: {
      default: false,
      type: 'boolean'
    },
    borderColor: {
      default: '',
      type: 'string'
    },
    borderStyle: {
      default: '',
      type: 'string'
    },
    baseStyle: {
      default: '',
      type: 'string'
    },
    url: {
      attribute: 'href',
      selector: 'a',
      source: 'attribute',
      type: 'string'
    },
    linkTarget: {
      attribute: 'target',
      selector: 'a',
      source: 'attribute',
      type: 'string'
    },
    coverImagesOption: {
      default: false,
      type: 'boolean'
    },
    backgroundColor: {
      default: '',
      type: 'string'
    },
    bookCoverBlockImages: {
      default: [],
      type: 'array'
    },
    hasImageWithTextParentBlock: {
      default: [],
      type: 'array'
    },
    isParentBlock: {
      default: false,
      type: 'boolean'
    }
  },
  save({ attributes }) {
    const {
      image,
      mobileImage,
      imageSize,
      blurEffect,
      borderColor,
      borderStyle,
      baseStyle,
      url,
      linkTarget,
      coverImagesOption,
      backgroundColor,
      bookCoverBlockImages,
      hasImageWithTextParentBlock,
    } = attributes;

    const isEmptyImage = isEmpty( image );

    const borderColorForBaseStyles = hasImageWithTextParentBlock.length === 0 && borderColor ? { color: borderColor } : undefined;

    const blockProps = useBlockProps.save({
      className: 'block__featured-image',
      style: { ...borderColorForBaseStyles }
    });

    const { children, ...innerBlocksProps } = useInnerBlocksProps.save(
      { className: `inner-blocks-wrapper ${coverImagesOption && !isEmptyImage ? 'absolute' : ''}` }
    );

    const hasBlur = !isEmptyImage && blurEffect ? 'image-has-blur' : '';
    const isCurved = baseStyle === 'has-curve';
    const curvedBaseWithBorder = ( isCurved && borderColor ) ? 'has-curved-border' : '';

    const hasCovers = coverImagesOption && bookCoverBlockImages.length > 0 ? 'has-covers' : '';
    const hasOneCover = coverImagesOption && bookCoverBlockImages.length === 1 ? 'has-one-cover' : '';
    const hasTwoCovers = coverImagesOption && bookCoverBlockImages.length === 2 ? 'has-two-covers' : '';
    const hasThreeCovers = coverImagesOption && bookCoverBlockImages.length === 3 ? 'has-three-covers' : '';

    const bgColor = backgroundColor && coverImagesOption ? { backgroundColor } : undefined;

    const featuredImageInnerClasses = filterOutWhiteSpace( [
      'featured-image--inner',
      imageSize,
      baseStyle,
      imageSize,
      curvedBaseWithBorder,
      borderStyle,
      hasBlur,
      hasCovers,
      hasOneCover,
      hasTwoCovers,
      hasThreeCovers
    ] );

    return (
      <div { ...blockProps }>
        <div className={ featuredImageInnerClasses } style={ { ...bgColor } }>
          { !isEmptyImage && (
            <div className="image-wrapper">
              <ConditionalWrapper
                condition={ url }
                wrapper={ ( img ) => <a href={ url } target={ linkTarget ? '_blank' : null } rel={ linkTarget ? 'noopener noreferrer' : null }>{ img }</a> }
              >
                <picture>
                  { mobileImage.sizes?.full?.url && (
                    <source srcSet={ mobileImage.sizes.full?.url } media="(max-width: 375px)" />
                  ) }
                  { image.sizes?.full.url && (
                    <source srcSet={ image.sizes.full?.url } media="(max-width: 1024px)" />
                  ) }
                  <img className="featured-image__image" src={ image?.sizes?.full?.url } alt={ image?.alt } loading='lazy' />
                </picture>

              </ConditionalWrapper>
            </div>
          ) }
          { coverImagesOption && (
            <div { ...innerBlocksProps }>
              <div className="books-wrapper">
                { children }
              </div>
            </div>
          ) }
        </div>
      </div>
    );
  }
};

const v2 = {
  supports: {
    anchor: true,
    html: false
  },
  attributes: {
    image: {
      default: {},
      type: 'object'
    },
    mobileImage: {
      default: {},
      type: 'object'
    },
    imageSize: {
      default: 'large',
      type: 'string'
    },
    blurEffect: {
      default: false,
      type: 'boolean'
    },
    borderColor: {
      default: '',
      type: 'string'
    },
    borderStyle: {
      default: '',
      type: 'string'
    },
    baseStyle: {
      default: '',
      type: 'string'
    },
    url: {
      attribute: 'href',
      selector: 'a',
      source: 'attribute',
      type: 'string'
    },
    linkTarget: {
      attribute: 'target',
      selector: 'a',
      source: 'attribute',
      type: 'string'
    },
    coverImagesOption: {
      default: false,
      type: 'boolean'
    },
    backgroundColor: {
      default: '',
      type: 'string'
    },
    bookCoverBlockImages: {
      default: [],
      type: 'array'
    },
    hasImageWithTextParentBlock: {
      default: [],
      type: 'array'
    }
  },
  save({ attributes }) {
    const {
      image,
      mobileImage,
      imageSize,
      blurEffect,
      borderColor,
      borderStyle,
      baseStyle,
      url,
      linkTarget,
      coverImagesOption,
      backgroundColor,
      bookCoverBlockImages,
      hasImageWithTextParentBlock
    } = attributes;

    const isEmptyImage = isEmpty( image );

    const borderColorForBaseStyles = hasImageWithTextParentBlock.length === 0 && borderColor ? { color: borderColor } : undefined;

    const blockProps = useBlockProps.save({
      className: 'block__featured-image',
      style: { ...borderColorForBaseStyles }
    });

    const { children, ...innerBlocksProps } = useInnerBlocksProps.save(
      { className: `inner-blocks-wrapper ${coverImagesOption && !isEmptyImage ? 'absolute' : ''}` }
    );

    const hasBlur = !isEmptyImage && blurEffect ? 'image-has-blur' : '';
    const isCurved = baseStyle === 'has-curve';
    const curvedBaseWithBorder = ( isCurved && borderColor ) ? 'has-curved-border' : '';

    const hasCovers = coverImagesOption && bookCoverBlockImages.length > 0 ? 'has-covers' : '';
    const hasOneCover = coverImagesOption && bookCoverBlockImages.length === 1 ? 'has-one-cover' : '';
    const hasTwoCovers = coverImagesOption && bookCoverBlockImages.length === 2 ? 'has-two-covers' : '';
    const hasThreeCovers = coverImagesOption && bookCoverBlockImages.length === 3 ? 'has-three-covers' : '';

    const bgColor = backgroundColor && coverImagesOption ? { backgroundColor } : undefined;

    const featuredImageInnerClasses = filterOutWhiteSpace( [
      'featured-image--inner',
      imageSize,
      baseStyle,
      imageSize,
      curvedBaseWithBorder,
      borderStyle,
      hasBlur,
      hasCovers,
      hasOneCover,
      hasTwoCovers,
      hasThreeCovers
    ] );

    return (
      <div { ...blockProps }>
        <div className={ featuredImageInnerClasses } style={ { ...bgColor } }>
          { !isEmptyImage && (
            <div className="image-wrapper">
              <ConditionalWrapper
                condition={ url }
                wrapper={ ( img ) => <a href={ url } target={ linkTarget ? '_blank' : null } rel={ linkTarget ? 'noopener noreferrer' : null }>{ img }</a> }
              >
                <picture>
                  { mobileImage.sizes?.full?.url && (
                    <source srcSet={ mobileImage.sizes.full?.url } media="(max-width: 375px)" />
                  ) }
                  { image.sizes?.full.url && (
                    <source srcSet={ image.sizes.full?.url } media="(max-width: 1024px)" />
                  ) }
                  <img className="featured-image__image" src={ image?.sizes?.full?.url } alt={ image?.alt } loading='lazy' />
                </picture>

              </ConditionalWrapper>
            </div>
          ) }
          { coverImagesOption && (
            <div { ...innerBlocksProps }>
              <div className="books-wrapper">
                { children }
              </div>
            </div>
          ) }
        </div>
      </div>
    );
  }
};

const v1 = {
  supports: {
    anchor: true,
    html: false
  },
  attributes: {
    image: {
      default: {},
      type: 'object'
    },
    mobileImage: {
      default: {},
      type: 'object'
    },
    blurEffect: {
      default: false,
      type: 'boolean'
    },
    borderColor: {
      default: '',
      type: 'string'
    },
    borderStyle: {
      default: '',
      type: 'string'
    },
    baseStyle: {
      default: '',
      type: 'string'
    },
    url: {
      attribute: 'href',
      selector: 'a',
      source: 'attribute',
      type: 'string'
    },
    linkTarget: {
      attribute: 'target',
      selector: 'a',
      source: 'attribute',
      type: 'string'
    },
    coverImagesOption: {
      default: false,
      type: 'boolean'
    },
    backgroundColor: {
      default: '',
      type: 'string'
    },
    bookCoverBlockImages: {
      default: [],
      type: 'array'
    },
    hasImageWithTextParentBlock: {
      default: [],
      type: 'array'
    }
  },
  save({ attributes }) {
    const {
      image,
      mobileImage,
      blurEffect,
      borderColor,
      borderStyle,
      baseStyle,
      url,
      linkTarget,
      coverImagesOption,
      backgroundColor,
      bookCoverBlockImages,
      hasImageWithTextParentBlock
    } = attributes;

    const isEmptyImage = isEmpty( image );

    const borderColorForBaseStyles = hasImageWithTextParentBlock.length === 0 && borderColor ? { color: borderColor } : undefined;

    const blockProps = useBlockProps.save({
      className: 'block__featured-image',
      style: { ...borderColorForBaseStyles }
    });

    const { children, ...innerBlocksProps } = useInnerBlocksProps.save(
      { className: `inner-blocks-wrapper ${coverImagesOption && !isEmptyImage ? 'absolute' : ''}` }
    );

    const hasBlur = !isEmptyImage && blurEffect ? 'image-has-blur' : '';
    const isCurved = baseStyle === 'has-curve';
    const curvedBaseWithBorder = ( isCurved && borderColor ) ? 'has-curved-border' : '';

    const hasCovers = coverImagesOption && bookCoverBlockImages.length > 0 ? 'has-covers' : '';
    const hasOneCover = coverImagesOption && bookCoverBlockImages.length === 1 ? 'has-one-cover' : '';
    const hasTwoCovers = coverImagesOption && bookCoverBlockImages.length === 2 ? 'has-two-covers' : '';
    const hasThreeCovers = coverImagesOption && bookCoverBlockImages.length === 3 ? 'has-three-covers' : '';

    const bgColor = backgroundColor && coverImagesOption ? { backgroundColor } : undefined;

    const featuredImageInnerClasses = filterOutWhiteSpace( [
      'featured-image--inner',
      baseStyle,
      curvedBaseWithBorder,
      borderStyle,
      hasBlur,
      hasCovers,
      hasOneCover,
      hasTwoCovers,
      hasThreeCovers
    ] );

    return (
      <div { ...blockProps }>
        <div className={ featuredImageInnerClasses } style={ { ...bgColor } }>
          { !isEmptyImage && (
            <div className="image-wrapper w-full h-full">
              <ConditionalWrapper
                condition={ url }
                wrapper={ ( img ) => <a href={ url } target={ linkTarget ? '_blank' : null } rel={ linkTarget ? 'noopener noreferrer' : null }>{ img }</a> }
              >
                <picture>
                  { mobileImage.sizes?.full?.url && (
                    <source srcSet={ mobileImage.sizes.full?.url } media="(max-width: 375px)" />
                  ) }
                  { image.sizes?.full.url && (
                    <source srcSet={ image.sizes.full?.url } media="(max-width: 1024px)" />
                  ) }
                  <img className='featured-image__image object-cover w-full h-full object-center' src={ image?.sizes?.full?.url } alt={ image?.alt } loading='lazy' />
                </picture>

              </ConditionalWrapper>
            </div>
          ) }
          { coverImagesOption && (
            <div { ...innerBlocksProps }>
              { children }
            </div>
          ) }
        </div>
      </div>
    );
  }
};

export default [ v3, v2, v1 ];
